import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { css } from "styled-components";
import TextArea from "@insite/mobius/TextArea";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import useRequestForQuotesForm from "@WhiteCap/Hooks/useRequestForQuotesForm";
import getColor from "@insite/mobius/utilities/getColor";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { connect } from "react-redux";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import siteMessage from "@insite/client-framework/SiteMessage";


{/* EC-235 : SubTask - EC-527 = Wizard Structure of the form (Level 1) i.e Customer Basic Info */}

// Mapping Redux state to props to determine if the user is authenticated
const mapStateToProps = (state: CustomApplicationState) => {
    const session = getSession(state);
    const currentUrl = getLocation(state);
    return {
        isAuthenticated: session.isAuthenticated, 
        currentUrl,
    };
};

// Type definition for component props, including Redux state and history
type Props = ReturnType<typeof mapStateToProps> & HasHistory;

// Interface for defining styles for the form elements
export interface QouteCartFormStyles {
    formHeading?: InjectableCss;
    formContainer?: InjectableCss;
	formInner?: InjectableCss;
    btnWrapper?: InjectableCss;
    commentsTextArea?: InjectableCss;
    sendToCopyEmail?: InjectableCss;
    recipientEmailTooltip?: TooltipPresentationProps;
}


// Defining default styles for the form components
export const qouteCartFormStyles: QouteCartFormStyles = {
   formContainer:{
	 css: css`
		border: 1px solid ${getColor("secondary")};
		margin-top: 30px;
	 `
   },
   formHeading:{
		css: css`
			padding: 15px 0px 15px 20px;
			background: ${getColor("secondary")};
            border-bottom: 1px solid ${getColor("secondary")};
			h3{
				color: ${getColor("text.main")};
				font-size: 18px;
				font-style: normal;
				font-weight: 700;
				line-height: 26px;
                ${({ theme }) =>
                breakpointMediaQueries(
					theme,
						[
							css`
								font-size: 16px;
							`,
							css`
								font-size: 16px;
							`,
							null,
							null,
							null,
						],
					"max",
           		)}
			}
            ${({ theme }) =>
                breakpointMediaQueries(
                theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                    ],
                "max",
            )}
		`
   },
   formInner:{
		css: css`
			padding: 20px 30px 20px;
            label{
                font-weight: 500;
            }
		`	
   },
   btnWrapper:{
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 30px;
            @media screen and (max-width: 390px) {
              button{
                span{
                    font-size: 11px !important;
                }
              }
            }
        `
   },
   commentsTextArea:{
        css: css`
            background: ${getColor("primary.contrast")} !important;
            height: 100px !important;
            border: 1px solid ${getColor("secondary")} !important;
            border-radius: 1px !important;
            font-size: 15px !important;
            line-height: 1.42857143 !important;
            font-weight: 400 !important;
            width: 100% !important;
            border-width: 1px !important;
            &:focus {
                outline: none !important;
                padding: 10px 10px 8px 10px !important;
            }
        `
   },   
    sendToCopyEmail:{
        css: css`
            position: relative;
        `
    },
    recipientEmailTooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;
            `,
            tooltipContainer: css`
                width: 200px;
                margin-left: 0px;
                &::after {
                    left: 150px;
                }
            `,
        },
        typographyProps: {
            weight: "normal",
        },
    },
};

// Function to check if all required fields for guest users are filled
// This will be used in Billing and Shipping Section (CustomerBillingAndShippingInfo.tsx)
export const isGuestUserBasicInfoCompleted = (): boolean => {
    // Get RFQ form data form session storage.
    const formData = JSON.parse(sessionStorage.getItem("guestRfqFormData") || "{}");
    const requiredFields = ["customerName", "companyName", "emailAddress", "confirmEmailAddress", "phoneNumber"];
    return requiredFields.every((field) => !!formData[field]); // All fields must have valid values
};

const styles = qouteCartFormStyles; // Assigns default styles
const StyledForm = getStyledWrapper("form");  // Creates a styled wrapper for the form

const GuestUserBasicInfoForm = ({ history, isAuthenticated, currentUrl }: Props) => {
    
    const guestUserAddedProduct = localStorage.getItem("rfqCart"); 


    useEffect(() => {
            // show an alert message when the user tries to close the browser
            window.addEventListener("beforeunload", alertUser);
            return () => {
                window.removeEventListener("beforeunload", alertUser);
            };
    }, []);

    
    const alertUser = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
    };
     // Handle redirection based on authentication and cart status
    useEffect(() => {
        const cart = guestUserAddedProduct ? JSON.parse(guestUserAddedProduct) : null;
        const isCartEmpty = !cart || cart.quoteCount <= 0;

        if (currentUrl.pathname.includes("/Content/Page")) {
			// Don't do anything, just display the page for content admin
			return;
		}

        if (!isAuthenticated && isCartEmpty) {
            // If user is not authenticated and cart is empty
            history.push("/quote-cart");
        } else if (isAuthenticated) {
            // If user is authenticated and cart is not empty and clear local storage
            history.push("/");
            localStorage.removeItem("rfqCart");
			sessionStorage.removeItem("guestRfqFormData");
        }
        
    }, [history, guestUserAddedProduct, isAuthenticated]);

     // Custom hook to manage form state and validation
    const { formData, errors, handleChange, validateForm, setFormDataInLocalStorage, handlePhoneNumberChange, inputRefs} = useRequestForQuotesForm();

     // Event handler to validate the form and navigate to the next page
    const saveAndContinue = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        const isValid = validateForm(["customerName", "companyName", "emailAddress", "confirmEmailAddress", "phoneNumber", "sendCopyToEmail"]);
        
        if (isValid) {
            setFormDataInLocalStorage();
            history.push("/guest-user-billing-info");
        }
    };

    // Event handler to navigate back to the Quote Cart page
    const backToQuoteCart = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
		history.push("/quote-cart");
    }
    
    return (
       <StyledWrapper >
            <StyledForm noValidate>
                {/* Customer Information Section */}
                <StyledWrapper  {...styles.formContainer}>
                    <StyledWrapper {...styles.formHeading}>
                        <Typography as="h3">{translate("Customer Information")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formInner}>
                            <GridContainer>
                                <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        label={translate("Name")}
                                        placeholder={translate("Your Name")}
                                        data-test-selector="first_name"
                                        maxLength={500}
                                        value={formData.customerName}
                                        error={errors.customerName}
                                        ref={(el) => (inputRefs.current.customerName = el as HTMLInputElement)}
                                        onChange={(e) => handleChange("customerName", e.currentTarget.value)}
                                        required
                                    />
                                </GridItem>
                                <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        label={translate("Company")}
                                        data-test-selector="company_name"
                                        placeholder={translate("Your Company")}
                                        maxLength={200}
                                        value={formData.companyName}
                                        error={errors.companyName}
                                        ref={(el) => (inputRefs.current.companyName = el as HTMLInputElement)}
                                        onChange={(e) => handleChange("companyName", e.currentTarget.value)}
                                        required
                                    />
                                </GridItem>
                                <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        label={translate("Phone Number")}
                                        placeholder={translate("Your Phone Number")}
                                        data-test-selector="phone_number"
                                        value={formData.phoneNumber || ""} 
                                        error={errors.phoneNumber}
                                        ref={(el) => (inputRefs.current.phoneNumber = el as HTMLInputElement)}
                                         onChange={(e) => {
                                            const value = e.currentTarget.value;
                                            handlePhoneNumberChange(value);
                                        }}
                                        maxLength={15}
                                        required
                                    />
                                </GridItem>
                                
                                <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        type="email"
                                        label={translate("Email")}
                                        placeholder={translate("Your Email Address")}
                                        data-test-selector="email"
                                        value={formData.emailAddress}
                                        error={errors.emailAddress}
                                        ref={(el) => (inputRefs.current.emailAddress = el as HTMLInputElement)}
                                        onChange={(e) => handleChange("emailAddress", e.currentTarget.value)}
                                        maxLength={256}
                                        required
                                    />
                                </GridItem>
                                <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        type="email"
                                        label={translate("Confirm Email")}
                                        placeholder={translate("Confirm Email Address")}
                                        data-test-selector="confirm_email"
                                        value={formData.confirmEmailAddress}
                                        error={errors.confirmEmailAddress}
                                        ref={(el) => (inputRefs.current.confirmEmailAddress = el as HTMLInputElement)}
                                        onChange={(e) => handleChange("confirmEmailAddress", e.currentTarget.value)}
                                        maxLength={256}
                                        required
                                    />
                                </GridItem>
                                <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        label={translate("Customer Job/Tag Number")}
                                        placeholder={translate("Customer Job/Tag Number")}
                                        data-test-selector="customer_job_number"
                                        value={formData.customerJob} 
                                        onChange={(e) => handleChange("customerJob", e.currentTarget.value)}
                                        maxLength={200}
                                    />
                                </GridItem>
                                <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        label={translate("Customer Number")}
                                        placeholder={translate("Customer Number")}
                                        value={formData.customerNumber} 
                                        onChange={(e) => handleChange("customerNumber", e.currentTarget.value)}
                                        data-test-selector="phone"
                                        maxLength={100}
                                    />
                                </GridItem>
                                {/* EC-1018 Remove Customer PO from-Guest-Quote form */}
                                {/* <GridItem width={[12, 12, 4, 4, 4]} >
                                    <TextField
                                        label={translate("Customer PO")}
                                        placeholder={translate("Customer PO")}
                                        data-test-selector="customer_po"
                                        value={formData.customerPo} 
                                        onChange={(e) => handleChange("customerPo", e.currentTarget.value)}
                                        maxLength={100}
                                    />
                                </GridItem> */}
                                
                            </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>
                {/* EC-898 [FE] Add notes field to Guest Quote Form */}
                  {/* Order Note Section */}
                  <StyledWrapper  {...styles.formContainer}>
                    <StyledWrapper {...styles.formHeading}>
                        <Typography as="h3">{translate("Order Notes")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formInner}>
                        <GridContainer>
                        
                            <GridItem width={[12, 12, 4, 4, 4]} >
                               <TextArea
                                    {...styles.commentsTextArea}
                                    label={translate("Note")}
                                    placeholder={translate("Note")}
                                    data-test-selector="Note"
                                    value={formData.orderNotes} 
                                    onChange={(e) => handleChange("orderNotes", e.currentTarget.value)}
                                    maxLength={500}
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>
                  {/* Send Copy Section */}
                <StyledWrapper  {...styles.formContainer}>
                    <StyledWrapper {...styles.formHeading}>
                        <Typography as="h3">{translate("Send Copy To")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formInner}>
                        <GridContainer>
                            <GridItem width={[12, 12, 4, 4, 4]}  {...styles.sendToCopyEmail}>
                                <TextField
                                    type="email"
                                    label={
                                        <>
                                            {translate("Email")}
                                            <Tooltip
                                                {...styles.recipientEmailTooltip}
                                                text={siteMessage("Lists_Use_Comma_As_Delimiter") as string}
                                            />
                                        </>
                                    }
                                    placeholder={translate("example@email.com, example2@email.com")}
                                    data-test-selector="email"
                                    value={formData.sendCopyToEmail} 
                                    onChange={(e) => handleChange("sendCopyToEmail", e.currentTarget.value)}
                                    ref={(el) => (inputRefs.current.sendCopyToEmail = el as HTMLInputElement)}
                                    error={errors.sendCopyToEmail}
                                    maxLength={256}
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 4, 4, 4]} >
                               <TextArea
                                    {...styles.commentsTextArea}
                                    label={translate("Comments")}
                                    placeholder="Comments"
                                    data-test-selector="comments"
                                    value={formData.sendCopyToComment} 
                                    onChange={(e) => handleChange("sendCopyToComment", e.currentTarget.value)}
                                    maxLength={500}
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>
                 {/* Button Section */}
                <StyledWrapper {...styles.btnWrapper}>
                    <Button variant="secondary" onClick={backToQuoteCart}>
                        {translate("Back To Quote Cart")}
                    </Button>
                    <Button onClick={saveAndContinue}>
                        {translate("Save & Continue")}
                    </Button>
                </StyledWrapper>
            </StyledForm>
	   </StyledWrapper>
    );
};

// Widget definition for the form
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(GuestUserBasicInfoForm)),
    definition: {
        displayName: "Guest User Basic Information Form",
        group: "Common",
    },
};

export default widgetModule;

{/* EC-235 : SubTask - EC-527 = Wizard Structure of the form (Level 1) i.e Customer Basic Info */}